import * as Types from '../types-api.generated';

import { MarketNftGridFields, UserLightFragment, MomentStatsFragment, MediaFragment, EditionSaleConfigurationFragment, DropSaleConfigurationFragment, WorldOverviewFragment } from '../api-fragments.generated';
import { useQuery, useInfiniteQuery, UseQueryOptions, UseInfiniteQueryOptions, InfiniteData } from '@tanstack/react-query';
import { apiFetcher } from 'lib/clients/graphql';

import { ClientError } from "graphql-request";
import { Address } from "viem";
import { ChainId } from "lib/chains";

export type ApiWorldOverviewVariables = Types.Exact<{
  id: Types.Scalars['Int'];
  listingsPerPage?: Types.InputMaybe<Types.Scalars['Limit']>;
}>;


export type ApiWorldOverview = { listings: { nfts: { page: number, totalPages: number, totalItems: number, items: Array<{ chainId: ChainId, name: string | null, id: string, tokenId: number, contractAddress: Address, saleStartsAt: string | null, creator: { name: string | null, profileImageUrl: string | null, username: string | null, publicKey: Address }, activeAuction: { duration: number, id: number, reservePrice: number, state: Types.ApiAuctionState, expiryDate: string | null, highestBid: { amount: number, bidder: { name: string | null, profileImageUrl: string | null, username: string | null, publicKey: Address } } | null } | null, activeBuyNow: { amount: number } | null, activeOffer: { amount: number, expiryDate: string } | null, media: { __typename: 'ImageMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, imageMimeType: Types.ApiImageMimeType } | { __typename: 'ModelMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, modelMimeType: Types.ApiModelMimeType, modelStaticUrl: string } | { __typename: 'VideoMedia', previewUrl: string | null, processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, staticUrl: string | null, url: string, videoMimeType: Types.ApiVideoMimeType } | null }> } }, drops: { drops: { items: Array<{ chainId: ChainId, id: string, name: string, mintPrice: number | null, clearingPrice: number | null, saleType: Types.ApiCollectionSaleType | null, slug: string, nftCount: number, earlyAccessStartTime: string | null, generalAvailabilityStartTime: string | null, contractType: Types.ApiContractType, isRevealed: boolean | null, contractAddress: Address, status: Types.ApiDropAvailabilityStatus, creator: { name: string | null, profileImageUrl: string | null, username: string | null, publicKey: Address }, media: { __typename: 'ImageMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, imageMimeType: Types.ApiImageMimeType } | { __typename: 'ModelMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, modelMimeType: Types.ApiModelMimeType, modelStaticUrl: string } | { __typename: 'VideoMedia', previewUrl: string | null, processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, staticUrl: string | null, url: string, videoMimeType: Types.ApiVideoMimeType } | null, saleConfiguration: { __typename: 'FixedPriceSaleConfiguration', maxTokenId: number, mintPrice: number, presaleStartTime: string | null, startTime: string, fndFixedPriceSaleStatus: Types.ApiFixedPriceStatus } | { __typename: 'HighlightFixedPriceSaleConfiguration', highlightId: string, mintPrice: number, startTime: string, optionalEndTime: string | null, optionalMaxTokenId: number | null, vectorId: number, highlightFixedPriceSaleStatus: Types.ApiHighlightFixedPriceStatus } | { __typename: 'HighlightLinearDutchAuctionSaleConfiguration' } | { __typename: 'HighlightStaggeredDutchAuctionSaleConfiguration', clearingPrice: number | null, endTime: string, highlightId: string, mintVectorId: string, maxPrice: number, maxTokenId: number, minPrice: number, mintEndTime: string | null, startTime: string, highlightStaggeredDutchAuctionStatus: Types.ApiHighlightDutchAuctionStatus, prices: Array<{ endTime: string, price: number }> } | { __typename: 'LinearDutchAuctionSaleConfiguration', clearingPrice: number | null, endTime: string, maxPrice: number, maxTokenId: number, minPrice: number, startTime: string, fndLinearDutchAuctionStatus: Types.ApiDutchAuctionStatus } | null }> } }, editions: { editions: { items: Array<{ chainId: ChainId, name: string, id: string, nftCount: number, slug: string, assetUrl: string | null, contractAddress: Address, mimeType: string | null, contractType: Types.ApiContractType, creator: { profileImageUrl: string | null, username: string | null, name: string | null, publicKey: Address }, editionSale: { __typename: 'LimitedEditionSaleConfiguration', maxTokenId: number, mintPrice: number, startTime: string, limitedEditionStatus: Types.ApiLimitedEditionStatus } | { __typename: 'TimedEditionSaleConfiguration', endTime: string, mintPrice: number, startTime: string, timedEditionStatus: Types.ApiTimedEditionStatus } }> } }, soldNfts: { items: Array<{ chainId: ChainId, name: string | null, id: string, tokenId: number, contractAddress: Address, sale: { amount: number, date: string, worldId: number | null }, media: { __typename: 'ImageMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, imageMimeType: Types.ApiImageMimeType } | { __typename: 'ModelMedia', processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, url: string, modelMimeType: Types.ApiModelMimeType, modelStaticUrl: string } | { __typename: 'VideoMedia', previewUrl: string | null, processingStatus: Types.ApiMediaProcessingStatus, sourceUrl: string | null, staticUrl: string | null, url: string, videoMimeType: Types.ApiVideoMimeType } | null, creator: { profileImageUrl: string | null, username: string | null, name: string | null, publicKey: Address } }> } };



export const ApiWorldOverviewDocument = /*#__PURE__*/ `
    query WorldOverview($id: Int!, $listingsPerPage: Limit = 4) {
  listings: nftsSearchV2(exhibitionId: $id, page: 0, perPage: $listingsPerPage) {
    nfts {
      page
      totalPages
      totalItems
      items {
        ...MarketNftGridFields
      }
    }
  }
  drops: dropsByExhibition(exhibitionId: $id, page: 0, perPage: 3) {
    drops {
      items {
        chainId
        id
        name
        status: availabilityStatus
        mintPrice
        clearingPrice
        saleType
        slug
        nftCount
        earlyAccessStartTime
        generalAvailabilityStartTime
        contractType
        isRevealed
        contractAddress
        creator {
          ...UserLightFragment
        }
        media {
          ...MediaFragment
        }
        saleConfiguration {
          ...DropSaleConfigurationFragment
        }
      }
    }
  }
  editions: editionsByExhibition(exhibitionId: $id, page: 0, perPage: 3) {
    editions {
      items {
        chainId
        name
        id
        nftCount
        slug
        assetUrl
        contractAddress
        mimeType
        contractType
        creator {
          profileImageUrl
          publicKey: accountAddress
          username
          name
        }
        editionSale: saleConfiguration {
          ...EditionSaleConfigurationFragment
        }
      }
    }
  }
  soldNfts: exhibitionSoldNfts(exhibitionId: $id, page: 0, perPage: 4) {
    items {
      chainId
      name
      id
      tokenId
      contractAddress
      sale {
        amount
        date
        worldId
      }
      media {
        ...MediaFragment
      }
      creator {
        profileImageUrl
        publicKey: accountAddress
        username
        name
      }
    }
  }
}
    ${MarketNftGridFields}
${UserLightFragment}
${MediaFragment}
${DropSaleConfigurationFragment}
${EditionSaleConfigurationFragment}`;

export const useWorldOverview = <
      TData = ApiWorldOverview,
      TError = ClientError
    >(
      variables: ApiWorldOverviewVariables,
      options?: Omit<UseQueryOptions<ApiWorldOverview, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<ApiWorldOverview, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<ApiWorldOverview, TError, TData>(
      {
    queryKey: ['WorldOverview', variables],
    queryFn: apiFetcher<ApiWorldOverview, ApiWorldOverviewVariables>(ApiWorldOverviewDocument, variables),
    ...options
  }
    )};

useWorldOverview.getKey = (variables: ApiWorldOverviewVariables) => ['WorldOverview', variables];

export const useInfiniteWorldOverview = <
      TData = InfiniteData<ApiWorldOverview>,
      TError = ClientError
    >(
      variables: ApiWorldOverviewVariables,
      options: Omit<UseInfiniteQueryOptions<ApiWorldOverview, TError, TData>, 'queryKey'> & { queryKey?: UseInfiniteQueryOptions<ApiWorldOverview, TError, TData>['queryKey'] }
    ) => {
    
    return useInfiniteQuery<ApiWorldOverview, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? ['WorldOverview.infinite', variables],
      queryFn: (metaData) => apiFetcher<ApiWorldOverview, ApiWorldOverviewVariables>(ApiWorldOverviewDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

useInfiniteWorldOverview.getKey = (variables: ApiWorldOverviewVariables) => ['WorldOverview.infinite', variables];


useWorldOverview.fetcher = (variables: ApiWorldOverviewVariables, options?: RequestInit['headers']) => apiFetcher<ApiWorldOverview, ApiWorldOverviewVariables>(ApiWorldOverviewDocument, variables, options);
